import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Calendar, Dice5, Users } from "lucide-react";
import { Header } from "../../components/layout/Header";
import { Card } from "../../components/common/Card";
import { Button } from "../../components/common/Button";
import { LoadingSpinner } from "../../components/common/LoadingSpinner";
import { api } from "../../services/api";
import { Event, EventStatus, EventType } from "../../types";
import { COLORS } from "../../config/colors";
import { formatEventDate, toDate } from "../../utils/date";

const EVENT_TYPE_LABELS: Record<EventType, string> = {
  [EventType.CASUAL_ROUND]: "Casual Round",
  [EventType.LEAGUE_GAME]: "League Game",
  [EventType.TOURNAMENT]: "Tournament",
  [EventType.MATCH]: "Match",
  [EventType.TRIP]: "Trip",
  [EventType.OUTING]: "Outing",
  [EventType.PARIMUTUEL_BETTING]: "Parimutuel Betting",
};

const STATUS_CHIP: Record<EventStatus, { label: string; cls: string }> = {
  [EventStatus.DRAFT]: { label: "Draft", cls: "bg-gray-100 text-gray-500" },
  [EventStatus.SCHEDULED]: {
    label: "Scheduled",
    cls: "bg-blue-100 text-blue-700",
  },
  [EventStatus.IN_PROGRESS]: {
    label: "Live",
    cls: "bg-green-100 text-green-700",
  },
  [EventStatus.COMPLETED]: {
    label: "Completed",
    cls: "bg-gray-100 text-gray-500",
  },
  [EventStatus.CANCELLED]: {
    label: "Cancelled",
    cls: "bg-red-100 text-red-500",
  },
};

export default function EventsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<"upcoming" | "past">("upcoming");
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const res = (await api.events.list()) as { data?: Event[] };
        setEvents(res.data ?? []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  // Split events: upcoming = in-progress OR (scheduled/draft with a future date)
  // Events with no date and SCHEDULED/DRAFT status are treated as upcoming.
  const now = new Date();
  const upcoming = events.filter((e) => {
    if (e.status === EventStatus.IN_PROGRESS) return true;
    if (
      e.status === EventStatus.COMPLETED ||
      e.status === EventStatus.CANCELLED
    )
      return false;
    // SCHEDULED or DRAFT: check date
    const d = e.rounds[0]?.date ? toDate(e.rounds[0].date) : null;
    if (!d) return true; // no date → treat as upcoming
    return d >= now;
  });
  const past = events.filter((e) => {
    if (
      e.status === EventStatus.COMPLETED ||
      e.status === EventStatus.CANCELLED
    )
      return true;
    // SCHEDULED/DRAFT with a past date → show in past tab
    const d = e.rounds[0]?.date ? toDate(e.rounds[0].date) : null;
    return !!d && d < now && e.status !== EventStatus.IN_PROGRESS;
  });

  const displayed = tab === "upcoming" ? upcoming : past;

  return (
    <>
      <Header title='Events' />

      {/* Tabs */}
      <div className='flex border-b border-gray-200 bg-white sticky top-[60px] md:top-16 z-20'>
        {(["upcoming", "past"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={[
              "flex-1 py-3 text-sm font-semibold capitalize transition-colors",
              tab === t
                ? "text-primary-800 border-b-2 border-primary-800"
                : "text-gray-400 hover:text-gray-600",
            ].join(" ")}
          >
            {t}
          </button>
        ))}
      </div>

      <div className='px-4 py-4 space-y-3'>
        {loading ? (
          <LoadingSpinner />
        ) : displayed.length === 0 ? (
          <div className='text-center py-12'>
            <Calendar size={40} className='mx-auto text-gray-300 mb-3' />
            <p className='text-gray-500 text-sm'>No {tab} events</p>
            {tab === "upcoming" && (
              <Button
                variant='primary'
                size='sm'
                className='mt-4'
                onClick={() => navigate("/events/create")}
                leftIcon={<Plus size={16} />}
              >
                Create Event
              </Button>
            )}
          </div>
        ) : (
          displayed.map((event) => {
            const chip = STATUS_CHIP[event.status];
            const isPari = event.eventType === EventType.PARIMUTUEL_BETTING;
            return (
              <Card
                key={event.id}
                accent={isPari ? COLORS.parimutuel : COLORS.events}
                onClick={() => navigate(`/events/${event.id}`)}
              >
                <div className='flex items-start justify-between gap-2'>
                  <div className='flex-1 min-w-0'>
                    <div className='flex items-center gap-1.5'>
                      {isPari && (
                        <Dice5 size={14} style={{ color: COLORS.parimutuel }} />
                      )}
                      <p className='font-semibold text-primary-800 truncate'>
                        {event.name}
                      </p>
                    </div>
                    <p className='text-xs text-gray-500 mt-0.5'>
                      {EVENT_TYPE_LABELS[event.eventType]}
                      {event.rounds[0]?.courseName
                        ? ` · ${event.rounds[0].courseName}`
                        : ""}
                    </p>
                    {event.rounds[0]?.date && (
                      <p className='text-xs text-secondary-400 font-medium mt-1'>
                        {formatEventDate(event.rounds[0].date)}
                      </p>
                    )}
                  </div>
                  <span
                    className={`text-[11px] px-2 py-0.5 rounded-full font-medium shrink-0 ${chip.cls}`}
                  >
                    {chip.label}
                  </span>
                </div>
                <div className='flex items-center gap-3 mt-2 text-xs text-gray-400'>
                  <span className='flex items-center gap-1'>
                    <Users size={12} /> {event.playerIds.length}
                  </span>
                </div>
              </Card>
            );
          })
        )}
      </div>

      {/* FAB */}
      <div className='fixed bottom-24 right-4 z-30'>
        <button
          onClick={() => navigate("/events/create")}
          className='w-14 h-14 rounded-full bg-secondary-400 text-white shadow-lg flex items-center justify-center active:scale-95 hover:bg-secondary-500'
          aria-label='Create event'
        >
          <Plus size={28} />
        </button>
      </div>
    </>
  );
}
